/**
 * ActivityBarHeader — Unified header zone of the Activity Bar.
 *
 * Renders a 2-column grid: title/status left, live indicators right.
 *
 * Left side:
 *   - Context header (back button + page title on patientDetail)
 *   - Transient status messages (recording started, mic switched, etc.)
 *
 * Right side (flush-right, compact):
 *   - Volume meter + timer (recording)
 *   - Progress bar + label (processing)
 *   - Collapses when idle
 *
 * On narrow screens (<768px), the grid stacks to a single column.
 *
 * Phase 6: Recording Status Bar — full-width row with status pill, timeline,
 * larger volume meter, and "elapsed" label. Red background during recording.
 * Right-justified layout with timer inside volume container.
 *
 * Phase 7: Merged StatusBand into header row for tighter vertical layout.
 * See: docs/features/infrastructure/app-shell.md
 */
import React, { useState, useEffect, useRef } from "react";
import { Check, AlertTriangle, MicOff, User } from "lucide-react";
import { RECORDING_STATUS_LABELS, RECORDING_STATUS_SEVERITY } from "../../utils/audioQualityUtils";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";

const STATUS_MESSAGE_TIMEOUT = 4000; // ms before transient message fades

/** Return "Morning", "Afternoon", or "Evening" based on the current hour. */
function getTimeOfDayGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return "Morning";
    if (hour < 17) return "Afternoon";
    return "Evening";
}

/** Get icon component for status pill */
function getStatusIcon(icon) {
    switch (icon) {
        case "checkmark":
            return <Check size={14} />;
        case "warning":
            return <AlertTriangle size={14} />;
        case "mic-off":
            return <MicOff size={14} />;
        case "person":
            return <User size={14} />;
        default:
            return null;
    }
}

/** Get CSS modifier class for severity */
function getSeverityClass(severity) {
    switch (severity) {
        case "good":
            return "ab-header-status-pill--good";
        case "warning":
            return "ab-header-status-pill--warning";
        case "error":
            return "ab-header-status-pill--error";
        default:
            return "ab-header-status-pill--neutral";
    }
}

/** Get timeline dot class for qcResult */
function getDotClass(qcResult) {
    switch (qcResult) {
        case "ok":
            return "ab-header-timeline-dot--good";
        case "low":
        case "silence":
        case "one_sided":
            return "ab-header-timeline-dot--warning";
        case "very_low":
        case "no_speech":
            return "ab-header-timeline-dot--error";
        default:
            return "ab-header-timeline-dot--empty";
    }
}

/**
 * @param {Object}  props
 * @param {string}  props.takeoverContext - "patientDetail" | "patientsList" | "dashboard" | null
 * @param {string}  props.patientName - Patient name for patientDetail context
 * @param {boolean} props.isViewOnly - Whether the patient is view-only (shared)
 * @param {Object|null} props.lockedContext - Locked context during active workflows
 *
 * Status indicator props (merged from StatusBand):
 * @param {boolean}  props.isRecording - Audio recording active
 * @param {boolean}  props.isPaused - Recording paused
 * @param {number}   props.elapsedSeconds - Recording timer
 * @param {number}   props.volumeLevel - 0–10
 *
 * Transient status messages:
 * @param {string}   props.statusMessage - Current transient status text
 *
 * Audio quality (Phase 3):
 * @param {string}   props.warningMessage - Inline audio quality warning text
 *
 * Recording Status UI (Phase 6):
 * @param {Array}    props.chunkStatusHistory - Array of { qcResult, speakerCount, timestamp }
 * @param {boolean}  props.localSilenceDetected - True when 10s+ of silence detected (from useRecordingSession)
 * @param {Object}   props.recordingStatus - { label, severity, icon, qcResult } from computeRecordingStatus
 * @param {boolean}  props.isActuallyStreaming - True only when actually recording (not during confirmation)
 */
const ActivityBarHeader = ({
    takeoverContext,
    patientName,
    isViewOnly = false,
    lockedContext = null,
    // Dashboard variant: centered header text
    isDashboard = false,
    // Status indicator props
    isRecording = false,
    isActuallyStreaming = false,
    isPaused = false,
    elapsedSeconds = 0,
    volumeLevel = 0,
    // Transient status
    statusMessage = "",
    // Audio quality (Phase 3)
    warningMessage = "",
    // Recording Status UI (Phase 6)
    chunkStatusHistory = [],
    localSilenceDetected = false,
    recordingStatus = null,
}) => {
    const { flags = {} } = useFeatureFlags();
    const debugMode = !!flags.debugMode;
    // Transient message fade
    const [visibleStatus, setVisibleStatus] = useState("");
    const [statusFading, setStatusFading] = useState(false);
    const fadeTimerRef = useRef(null);
    // Track which chunk index was just added (for animation)
    const [lastChunkCount, setLastChunkCount] = useState(0);

    useEffect(() => {
        if (statusMessage) {
            setVisibleStatus(statusMessage);
            setStatusFading(false);
            clearTimeout(fadeTimerRef.current);
            fadeTimerRef.current = setTimeout(() => {
                setStatusFading(true);
                // Clear text after fade animation
                setTimeout(() => setVisibleStatus(""), 300);
            }, STATUS_MESSAGE_TIMEOUT);
        }
        return () => clearTimeout(fadeTimerRef.current);
    }, [statusMessage]);

    // Track chunk count changes for animation
    useEffect(() => {
        if (chunkStatusHistory.length > lastChunkCount) {
            setLastChunkCount(chunkStatusHistory.length);
        }
    }, [chunkStatusHistory.length, lastChunkCount]);

    // Reset tracking when recording stops
    useEffect(() => {
        if (!isRecording) {
            setLastChunkCount(0);
        }
    }, [isRecording]);

    if (!takeoverContext && !isRecording && !visibleStatus) return null;

    // Use locked context values if present (activity lock)
    const displayName = lockedContext?.patientName || patientName;
    const displayText = takeoverContext ? (lockedContext?.headerText || (
        takeoverContext === "dashboard"
            ? `Good ${getTimeOfDayGreeting().toLowerCase()}, welcome to your workspace.`
            : null
    )) : null;

    // Format elapsed seconds as MM:SS
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, "0");
        const s = (seconds % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    const hasStatusIndicator = isRecording;

    // Build timeline dots from all chunks (grows over time)
    const timelineDots = chunkStatusHistory.map((chunk, i) => ({
        qcResult: chunk?.qcResult || null,
        isNew: i === chunkStatusHistory.length - 1 && chunkStatusHistory.length === lastChunkCount,
    }));

    return (
        <div className={`activity-bar-header-zone${isDashboard ? " ab-header-dashboard" : ""}`}>
            {/* Left column: title + transient status */}
            <div className={`ab-header-left${isDashboard ? " ab-header-left-centered" : ""}`}>
                {displayText && (
                    <div className="page-title userback-ignore ab-header-title">
                        {displayText}
                        {isViewOnly && (
                            <span className="ab-header-badge-view-only">
                                View Only
                            </span>
                        )}
                    </div>
                )}
                {/* Transient status message — show in left column when not recording */}
                {visibleStatus && !isRecording && (
                    <span className={`ab-header-status-message${statusFading ? " ab-header-status-fading" : ""}`}>
                        {visibleStatus}
                    </span>
                )}
            </div>

            {/* Recording Status Bar — full width with red treatment (Phase 6) */}
            {hasStatusIndicator && isRecording && (
                <div className="ab-header-recording">
                    {/* Timeline — grows to fill space, shows all chunk results with connecting lines */}
                    {debugMode && (
                    <div className="ab-header-timeline" aria-label="Recording quality history">
                        {timelineDots.map((dot, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <div className="ab-header-timeline-line" />}
                                <div
                                    className={`ab-header-timeline-dot ${getDotClass(dot.qcResult)}${dot.isNew ? " ab-header-timeline-dot--new" : ""}`}
                                />
                            </React.Fragment>
                        ))}
                    </div>
                    )}

                    {/* Status Pill — shows context-aware messages */}
                    {(() => {
                        // Determine which status to show
                        // Priority: Waiting > Audio Warning > Silence > Chunk QC > Analyzing/Listening
                        let statusLabel, statusSeverity, statusIcon;
                        
                        if (!isActuallyStreaming) {
                            // During confirmation dialog
                            statusLabel = RECORDING_STATUS_LABELS.waiting;
                            statusSeverity = "neutral";
                            statusIcon = null;
                        } else if (warningMessage && !isPaused) {
                            // Real-time audio warning (low volume, clipping, etc.)
                            statusLabel = warningMessage;
                            statusSeverity = "warning";
                            statusIcon = "warning";
                        } else if (localSilenceDetected) {
                            // Silence always takes precedence when actively recording
                            statusLabel = RECORDING_STATUS_LABELS.silence;
                            statusSeverity = RECORDING_STATUS_SEVERITY.silence;
                            statusIcon = "warning";
                        } else if (chunkStatusHistory.length === 0) {
                            // Recording but no chunks yet
                            statusLabel = RECORDING_STATUS_LABELS.analyzing;
                            statusSeverity = "neutral";
                            statusIcon = null;
                        } else if (recordingStatus) {
                            // Use chunk-based status
                            statusLabel = recordingStatus.label;
                            statusSeverity = recordingStatus.severity;
                            statusIcon = recordingStatus.icon;
                        } else {
                            statusLabel = RECORDING_STATUS_LABELS.listening;
                            statusSeverity = "neutral";
                            statusIcon = null;
                        }

                        return (
                            <div className={`ab-header-status-pill ${getSeverityClass(statusSeverity)}`}>
                                {getStatusIcon(statusIcon)}
                                <span className="ab-header-status-pill-text">{statusLabel}</span>
                            </div>
                        );
                    })()}

                    {/* Volume Meter + Timer combined in container */}
                    <div className="ab-header-volume-container" aria-label="Volume level">
                        <div className="ab-header-volume-meter">
                            {Array.from({ length: 10 }).map((_, i) => {
                                const isActive = i < volumeLevel;
                                const barHeight = ((i + 1) / 10) * 24;
                                const barColor = !isActive
                                    ? "var(--color-volume-inactive, rgba(255, 255, 255, 0.5))"
                                    : i >= 8
                                        ? "var(--color-warning-text)"
                                        : "var(--color-light-green)";
                                return (
                                    <div
                                        key={i}
                                        className={`ab-header-volume-bar${isActive ? " ab-header-volume-bar--active" : ""}`}
                                        style={{
                                            height: `${barHeight}px`,
                                            backgroundColor: barColor,
                                        }}
                                    />
                                );
                            })}
                        </div>
                        <div className="ab-header-timer-group">
                            <span className="ab-header-timer">{formatTime(elapsedSeconds)}</span>
                            <span className="ab-header-timer-label">elapsed</span>
                        </div>
                    </div>

                    {/* Paused indicator */}
                    {isPaused && <span className="ab-header-paused">Paused</span>}
                </div>
            )}
        </div>
    );
};

export default ActivityBarHeader;
